export class NumberUtils {
    static toCurrencyString(
        value?: number | null,
        decimals: number = 2,
        locale = 'pt-BR',
        currency = 'BRL',
    ): string {
        if (value === undefined || value === null || Number.isNaN(value)) return '-';
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(value);
    }

    static toDecimalString(
        value?: number | null,
        decimals: number = 2,
        locale = 'pt-BR',
    ): string {
        if (value === undefined || value === null || Number.isNaN(value)) return '-';
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(value);
    }

    /** Converts to number; returns undefined on null/undefined/empty string or NaN */
    static toNumberOrUndefined(value: unknown): number | undefined {
        if (value === undefined || value === null || value === '') return undefined;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? undefined : parsed;
    }

    /** Converts to finite number; returns undefined on NaN or infinite */
    static toNumber(value?: string | number | null): number | undefined {
        const parsed = this.toNumberOrUndefined(value);
        return Number.isFinite(parsed ?? NaN) ? parsed : undefined;
    }

    /** Parses positive integers honoring a minimum (rounded) value */
    static parsePositiveInt(value?: string | number | null, min = 0): number | undefined {
        const parsed = this.toNumber(value);
        if (parsed === undefined) return undefined;
        const normalized = Math.max(min, Math.round(parsed));
        return Number.isFinite(normalized) ? normalized : undefined;
    }

    /**
     * Formats a string input as currency using Intl.NumberFormat; defaults to BRL with pt-BR locale.
     * Strips non-digits, divides by 100 to include cents, returns empty string when no digits found.
     */
    static toCurrencyStringFromDigits(value: string, locale = 'pt-BR', currency = 'BRL'): string {
        const digits = (value ?? '').replace(/\D/g, '');
        if (!digits) return '';
        const amount = Number.parseInt(digits, 10) / 100;
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amount);
    }

    /** Parses a currency-like input string into a numeric value; strips non-digits and divides by 100. */
    static toCurrencyAmountFromDigits(value: string): number {
        const digits = (value ?? '').replace(/\D/g, '');
        if (!digits) return 0;
        return Number.parseInt(digits, 10) / 100;
    }

    /** Formats a byte count into a human-readable file size string (e.g. "1.5 MB", "320 KB"). */
    static formatFileSize(bytes?: number | null, decimals = 2): string {
        if (bytes === undefined || bytes === null || Number.isNaN(bytes) || bytes <= 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
    }
}

